/**
 * The title and story scenes on their own (spec 08 §1), for judging them in the real pipeline:
 *
 *   ?scene=title&view=title&t=1.5     the title at 1.5 s after it appeared (no t: live)
 *   ?scene=title&view=story           the story screen, its lines drawn as the session would
 *   &band=1                           outline the band the menus are drawn in (TITLE_LAYOUT)
 *   &text=0                           the story without its lines
 *   &ppt=40&fx=-4&fy=8                a close-up: texels a tile (at 640 wide), and where to look
 *   &look=modern|1987                 the player's display options as the game applies them (defaults
 *                                     otherwise; without it, the pipeline's own settings)
 *
 * Owned by the TITLE stream from M3 on.
 */

import { level } from '../levels';
import { GameView } from '../render/game/GameView';
import { levelLook } from '../render/levelLook';
import { Palette } from '../render/palette';
import { DEFAULT_SETTINGS } from '../settings';
import { STORY_LINES, storyRowY, TITLE_LAYOUT } from '../render/title';
import type { Banner } from '../ui/hud/messages';
import { chooseSize, createRenderer, exposeLook, num } from './devKit';

export function startTitleScene(canvas: HTMLCanvasElement, params: URLSearchParams): void {
  const view = new GameView(createRenderer(canvas), chooseSize(params), level(1));
  const story = params.get('view') === 'story';
  const look = params.get('look');
  if (look === 'modern' || look === '1987') view.setDisplay({ ...DEFAULT_SETTINGS.display, look });
  view.showBackdrop(story ? 'story' : 'title');
  if (params.has('ppt')) {
    view.camera.pixelsPerTile = num(params, 'ppt', 14) * view.camera.size.scale;
    view.camera.focus.set(num(params, 'fx', 0), num(params, 'fy', 0), 0);
    view.camera.update();
  }
  const palette = new Palette(levelLook(level(1)));
  const hud = { level: 1, hiScore: 20000, players: [
    { playing: false, score: 0, lives: 0, extend: [] },
    { playing: false, score: 0, lives: 0, extend: [] },
  ] } as const;
  // The story's lines where the C64 puts them (rows 0, 2, 4, 6), in its yellow, doubled.
  const banners: Banner[] = story && params.get('text') !== '0'
    ? STORY_LINES.map((l) => ({ lines: [l.text], colour: palette.hex('bolt.1'), scale: 2, y: storyRowY(l.row) }))
    : [];
  const band = params.get('band') === '1'
    ? (ctx: CanvasRenderingContext2D): void => {
      const { menuTop, menuBottom, menuWidth } = TITLE_LAYOUT;
      ctx.strokeStyle = palette.hex('hudAccent.0');
      ctx.strokeRect(320 - menuWidth / 2 + 0.5, menuTop + 0.5, menuWidth - 1, menuBottom - menuTop - 1);
    }
    : undefined;
  const frozen = params.has('t');
  const start = performance.now();
  // The view counts time from the first frame it is drawn: draw one at 0 so a frozen `t` means
  // "t seconds after the title appeared".
  view.render(null, new Map(), 1, 0, hud, [], band);
  const frame = (): void => {
    const t = frozen ? num(params, 't', 0) : (performance.now() - start) / 1000;
    view.render(null, new Map(), 1, t, hud, banners, band);
    if (!frozen) requestAnimationFrame(frame);
  };
  frame();
  exposeLook(view.pipeline, frame, { view }, view.hud.canvas);
}
